import { AsyncPipe, Location, NgStyle } from '@angular/common';
import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { debounceTime, Subscription, tap } from 'rxjs';
import { NullResultsGTMService } from '@uc/web/core';
import {
	fetchSearchAdvice,
	updateSearchParams,
} from '../../data-access/+state/advice.actions';
import { getStateAdviceSearch } from '../../data-access/+state/advice.selectors';
import {
	SortComponent,
	SortOptionModel,
	SortService,
} from '@uc/web/shared/feature/feature-filter';
import {
	LoadMoreBtnComponent,
	LoadMoreBtnService,
	MainHeaderComponent,
} from '@web/shared/ui';
import {
	LoadingAnimationComponent,
	LoadingAnimationService,
	SearchBarService,
	SearchComponent,
} from '@uc/shared/ui';
import { SeoMetadataService } from '@uc/utilities';
import { SadFaceSvgComponent } from '@uc/shared/svg';
import { ADVICE_SEARCH_META_DATA, SORT_OPTIONS } from '../../static/advice.properties';
import { AdviceFeaturedCardComponent } from '../../components/advice-featured-card/advice-featured-card.component';

@Component({
	selector: 'uc-advice-search',
	templateUrl: './advice-search.component.html',
	imports: [
		NgStyle,
		AsyncPipe,
		MainHeaderComponent,
		SearchComponent,
		LoadingAnimationComponent,
		AdviceFeaturedCardComponent,
		LoadMoreBtnComponent,
		SadFaceSvgComponent,
		SortComponent,
	],
})
export class AdviceSearchComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChildren('articleEle') articleRef!: QueryList<ElementRef>;
	sortOptions = [...SORT_OPTIONS];
	searchTerm = '';
	loadingSearch = true;
	loadingHtml!: string;
	loadMore = false;
	page = 1;
	articles: any[] = [];

	private _offset = 0;
	private _firstLoad = true;
	private _sortOptionSub!: Subscription;
	private _routerSub!: Subscription;
	private _timeoutId: any;
	//only show if search was made
	private _relevanceSearchOption = {
		id: 5,
		text: 'Relevance',
		key: 'content_title',
	};

	private _searchSub = this._searchSrv.inputValue$
		.pipe(debounceTime(500))
		.subscribe((search) => {
			this.addSearchTerm(search);
		});

	private _pageSub = this._loadBtnSrv.getLoadMorePage$.subscribe((payload) => {
		this.addPageNumber(payload?.page);
	});

	searchResult$ = this._store.select(getStateAdviceSearch).pipe(
		tap((result) => {
			const data = result?.data;

			if (data && data.length === 0) {
				// GTM Event
				this._nullResultsGTMSrv.nullResults(this.searchTerm);
			}

			if (data) {
				this.articles = this.configureArticles(data);
				this.configureSort();
				this.loadMore = false;
				this.loadingSearch = false;
			}
		}),
	);

	constructor(
		private _store: Store,
		private _route: ActivatedRoute,
		private _router: Router,
		private _location: Location,
		private _seoSrv: SeoMetadataService,
		private _sortSrv: SortService,
		private _searchSrv: SearchBarService,
		private _loadingSrv: LoadingAnimationService,
		private _loadBtnSrv: LoadMoreBtnService,
		private _nullResultsGTMSrv: NullResultsGTMService,
	) {}

	ngOnInit(): void {
		//seo
		this._seoSrv.updateTitle(ADVICE_SEARCH_META_DATA.title);
		this._seoSrv.updateDescription(ADVICE_SEARCH_META_DATA.description);

		//create loading animation
		this.loadingHtml = this._loadingSrv.createPara(2, 3);

		this._sortSrv.setDefaultOption(this.sortOptions[0]);

		//get search term from url
		this._routerSub = this._route.queryParams.subscribe((params: Params) => {
			const search = params['s'] ? params['s'].toLowerCase() : '';
			const page = parseInt(params['page']);
			this.page = page ? page : 1;

			if (search) this._searchSrv.onValueChange(search);
			if (this.page > 1) this._loadBtnSrv.setInitialPage(this.page);
			if (!search && this.page === 1) this.updateStore();
		});

		this._sortOptionSub = this._sortSrv.selectedOption$.subscribe(
			(sortOption: SortOptionModel) => {
				this.articles = this._sortSrv.onSortBy(
					sortOption.id,
					this.articles,
					sortOption.key,
					this.searchTerm,
				);
			},
		);
	}

	ngAfterViewInit(): void {
		//scroll to element
		if (this.page > 1 && this.articleRef?.length !== 0) {
			this._timeoutId = setTimeout(() => {
				const index = this.page * 20 - 20;
				const userToScrollOn = this.articleRef.toArray();
				userToScrollOn[index].nativeElement.scrollIntoView({
					behavior: 'smooth',
				});
			}, 1000);
		}
	}

	ngOnDestroy(): void {
		if (this._sortOptionSub) this._sortOptionSub.unsubscribe();
		if (this._routerSub) this._routerSub.unsubscribe();
		if (this._pageSub) this._pageSub.unsubscribe();
		if (this._searchSub) this._searchSub.unsubscribe();
		if (this._timeoutId) {
			clearTimeout(this._timeoutId);
		}
	}

	configureSort(): void {
		if (this.searchTerm) {
			//add relevance option at index 0
			if (this.sortOptions?.length === 2) {
				this.sortOptions.unshift(this._relevanceSearchOption);
			}

			this.articles = this._sortSrv.onSortBy(
				5,
				this.articles,
				'content_title',
				this.searchTerm,
			);
		} else {
			// filter based on newest
			if (this.sortOptions?.length > 2) {
				this.sortOptions.splice(0, 1);
			}
		}

		this._sortSrv.setDefaultOption(this.sortOptions[0]);
		this._sortSrv.onSortOption(this.sortOptions[0]);
	}

	configureArticles(data: any[]): any[] {
		let uniqueArticles = null;

		//if search input changed / reset, reset articles
		if (!this.loadMore) {
			uniqueArticles = data;
			//add articles and filter out dupes
		} else {
			const allArticles = [...this.articles, ...data];
			uniqueArticles = this.removeDuplicateArticles(allArticles);
		}

		return uniqueArticles;
	}

	removeDuplicateArticles(articles: any[]): any[] {
		return articles.filter(
			(value, index, self) =>
				index === self.findIndex((t) => t.art_id === value.art_id),
		);
	}

	updateUrl(): void {
		const basePath = this._router.url.split('?')[0];
		const currentPath = this._router.url;

		let newPath;

		//use location.go without triggering scrollPositionRestoration
		if (this.searchTerm && this.page > 1) {
			newPath = `${basePath}?s=${this.searchTerm}&page=${this.page.toString()}`;
		} else if (this.searchTerm && this.page === 1) {
			newPath = `${basePath}?s=${this.searchTerm}`;
		} else if (this.page > 1) {
			newPath = `${basePath}?page=${this.page.toString()}`;
		} else {
			newPath = `${basePath}`;
		}

		if (currentPath === newPath) return;

		this._location.go(newPath);
	}

	addPageNumber(page: number): void {
		this.page = page;
		this._offset = this._firstLoad ? 0 : 1;
		this.loadMore = true;
		this.updateStore();
	}

	addSearchTerm(searchTerm: string): void {
		if (searchTerm === this.searchTerm) return;

		this.searchTerm = searchTerm;
		this.page = 1;
		this._offset = 0;
		this.loadMore = false;
		this.updateStore();
	}

	updateStore(): void {
		if (!this.loadMore) this.loadingSearch = true;
		this.updateUrl();

		this._store.dispatch(fetchSearchAdvice(this.searchTerm, this.page, this._offset));

		this._store.dispatch(
			updateSearchParams({
				searchTerm: this.searchTerm,
				page: this.page,
				offset: this._offset,
			}),
		);

		if (this._firstLoad) this._firstLoad = false;
	}
}
